import React from "react";
import { Button, Drawer } from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import InsightsRoundedIcon from "@mui/icons-material/InsightsRounded";
import DonutLargeRoundedIcon from "@mui/icons-material/DonutLargeRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import CategoryRoundedIcon from "@mui/icons-material/CategoryRounded";
import FlagRoundedIcon from "@mui/icons-material/FlagRounded";
import AssignmentIndRoundedIcon from "@mui/icons-material/AssignmentIndRounded";
import DirectionsRunRoundedIcon from "@mui/icons-material/DirectionsRunRounded";
import { Avatar, BarList, CountUp, MixDonut, ProjectMark, SprintHistoryChart, WorkloadChart } from "./charts";
import { HealthPill } from "./ProjectCards";
import { daysLeftLabel, toSegments } from "./reportModel";

const Panel = ({ icon, title, children, className = "" }) => (
  <section className={`pr-panel ${className}`}>
    <h3 className="pr-panel__title">
      <span className="pr-panel__icon">{icon}</span>
      {title}
    </h3>
    {children}
  </section>
);

const ProjectDetail = ({ view, open, onClose, onOpenBoard }) => (
  <Drawer
    anchor="right"
    open={open}
    onClose={onClose}
    // the fixed top bar sits at drawer + 1, so lift the panel to the modal layer or it slides in underneath
    sx={{ zIndex: (theme) => theme.zIndex.modal }}
    PaperProps={{
      className: "pr-drawer",
      role: "dialog",
      "aria-label": view ? `${view.name} details` : "Project details",
    }}
    slotProps={{ backdrop: { className: "pr-backdrop" } }}
  >
    {view ? (
      <>
        <header className="pr-drawer__head" style={{ "--c": view.color }}>
          <ProjectMark name={view.name} color={view.color} size="lg" />
          <div className="pr-drawer__title">
            <h2>{view.name}</h2>
            <span>
              {[view.key, view.lead?.name ? `Lead: ${view.lead.name}` : null, `${view.team.length} members`].filter(Boolean).join(" · ")}
            </span>
          </div>
          <HealthPill view={view} />
          <button type="button" className="pr-icon-btn" onClick={onClose} aria-label="Close details">
            <CloseRoundedIcon />
          </button>
        </header>

        <div className="pr-drawer__body">
          <div className="pr-mini-kpis">
            <div>
              <span>{view.active ? "Sprint done" : "Last sprint"}</span>
              <strong>
                <CountUp value={view.completion} suffix="%" />
              </strong>
              <small>{view.focus ? `${view.focus.done}/${view.focus.total} · ${view.focus.name}` : "No sprints"}</small>
            </div>
            <div>
              <span>Open issues</span>
              <strong>
                <CountUp value={view.issues.open} />
              </strong>
              <small>{view.issues.unassigned === null ? `${view.issues.total} total` : `${view.issues.unassigned} unassigned`}</small>
            </div>
            <div>
              <span>Backlog</span>
              <strong>{view.issues.backlog === null ? "—" : <CountUp value={view.issues.backlog} />}</strong>
              <small>not in a sprint</small>
            </div>
            <div>
              <span>Time left</span>
              <strong>{view.active ? daysLeftLabel(view.daysLeft) || "—" : "—"}</strong>
              <small>{view.velocity === null ? "no completed sprints" : `velocity ${view.velocity}/sprint`}</small>
            </div>
          </div>

          <Panel icon={<InsightsRoundedIcon />} title="Sprint history">
            <SprintHistoryChart history={view.history} color={view.color} />
          </Panel>

          {view.sprintSummary ? (
            <Panel icon={<DonutLargeRoundedIcon />} title="Current sprint by status">
              <MixDonut segments={toSegments(view.sprintSummary.byStatus, "status")} totalLabel="in sprint" size={176} />
            </Panel>
          ) : null}

          {view.sprintSummary?.workload?.length ? (
            <Panel icon={<AssignmentIndRoundedIcon />} title="Workload this sprint">
              <WorkloadChart workload={view.sprintSummary.workload} />
            </Panel>
          ) : null}

          <div className="pr-panel-grid">
            <Panel icon={<CategoryRoundedIcon />} title="Issue types">
              <BarList segments={toSegments(view.issues.byType, "type")} emptyText="No issues yet" />
            </Panel>
            <Panel icon={<FlagRoundedIcon />} title="Priority">
              <BarList segments={toSegments(view.issues.byPriority, "priority")} emptyText="No issues yet" />
            </Panel>
          </div>

          <Panel icon={<GroupsRoundedIcon />} title={`Team (${view.team.length})`}>
            {view.team.length ? (
              <ul className="pr-team">
                {view.team.map((member, index) => (
                  <li key={member.id || member.name} style={{ "--i": index }}>
                    <Avatar name={member.name} size={32} />
                    <span>
                      <strong>{member.name}</strong>
                      <small>{member.role || member.email || "Member"}</small>
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="pr-muted">No one has been added to this project yet.</p>
            )}
          </Panel>
        </div>

        {view.slug ? (
          <footer className="pr-drawer__foot">
            <Button className="pr-btn pr-btn--primary" startIcon={<DirectionsRunRoundedIcon />} onClick={() => onOpenBoard(view)} disableRipple>
              Open sprint board
            </Button>
          </footer>
        ) : null}
      </>
    ) : null}
  </Drawer>
);

export default ProjectDetail;
